import { Link } from 'react-router-dom';
import { cn } from '@eo/shared';

import { ROLE_BAR_CLASS, ROLE_DOT_CLASS, ROLE_LABELS } from './constants';
import { formatHours, monthShare, teamLoadRows } from './helpers';
import { LoadBar } from './LoadBar';
import type { TeamLoadProps } from './types';

// One shared scale, so a longer bar is always more hours, person to person.
export function TeamLoad({ users, progress, personHref }: TeamLoadProps) {
  const rows = teamLoadRows(users);

  if (rows.length === 0) {
    return (
      <p className="text-sm text-text-tertiary">
        Nobody is assigned to or has logged on this portfolio this month.
      </p>
    );
  }

  const scale = Math.max(...rows.map((r) => Math.max(r.assigned, r.actual)));
  const share = monthShare(progress);

  return (
    <ul className="flex flex-col gap-5">
      {rows.map((row) => {
        const over = row.actual - row.assigned;
        return (
          <li key={row.userId} data-testid="team-load-row" className="flex flex-col gap-2">
            <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
              <span className="flex items-center gap-2 text-text-primary">
                <span className={cn('size-2.5 rounded-[2px]', ROLE_DOT_CLASS[row.role])} />
                <Link
                  to={personHref(row.userId)}
                  className="underline-offset-4 hover:underline focus-visible:underline"
                >
                  {row.name}
                </Link>
                <span className="text-xs text-text-tertiary">{ROLE_LABELS[row.role]}</span>
              </span>
              <span className="tabular-nums text-text-secondary">
                {row.assigned > 0
                  ? `${formatHours(row.actual)} of ${formatHours(row.assigned)}`
                  : `Not assigned · ${formatHours(row.actual)} logged`}
                {row.assigned > 0 && over > 0 && (
                  <span className="ml-2 text-destructive">{formatHours(over)} over</span>
                )}
              </span>
            </div>
            <LoadBar
              label={
                row.assigned > 0
                  ? `${row.name}: ${formatHours(row.actual)} logged of ${formatHours(row.assigned)} assigned`
                  : `${row.name}: ${formatHours(row.actual)} logged without an assignment`
              }
              scale={scale}
              target={row.assigned}
              pace={row.assigned * share}
              segments={[
                {
                  key: 'logged',
                  hours: row.actual,
                  className: ROLE_BAR_CLASS[row.role].logged,
                },
                {
                  key: 'remaining',
                  hours: Math.max(0, row.assigned - row.actual),
                  className: ROLE_BAR_CLASS[row.role].remaining,
                },
              ]}
            />
          </li>
        );
      })}
    </ul>
  );
}
